import {
  isBorder,
  isSolid,
  STEP_UP,
  stepOpen,
  surfaceAt,
  Tile,
  tileAt,
  variantAt,
  wallBetween,
  type GameMap,
  type Region,
} from './map';

export type Waypoint = [x: number, y: number];

/** True when a circle of radius r centered at (x, y) touches no solid tile. */
export function freeAt(m: GameMap, x: number, y: number, r = 0.28): boolean {
  return (
    !isSolid(m, Math.floor(x - r), Math.floor(y - r)) &&
    !isSolid(m, Math.floor(x + r), Math.floor(y - r)) &&
    !isSolid(m, Math.floor(x - r), Math.floor(y + r)) &&
    !isSolid(m, Math.floor(x + r), Math.floor(y + r))
  );
}

/**
 * What a jump cannot clear: cliffs (the border and mountainsides), buildings, the stele and the
 * sea. Boulders and trees can be leapt over.
 */
export function blocksJump(m: GameMap, i: number, j: number): boolean {
  const t = tileAt(m, i, j);
  return (
    t === Tile.Building ||
    t === Tile.Stele ||
    t === Tile.Water ||
    (t === Tile.Rock && (isBorder(m, i, j) || variantAt(m, i, j) >= 4))
  );
}

/** Like {@link freeAt}, for a fighter in the air high enough to clear low obstacles. */
export function clearAbove(m: GameMap, x: number, y: number, r = 0.28): boolean {
  return (
    !blocksJump(m, Math.floor(x - r), Math.floor(y - r)) &&
    !blocksJump(m, Math.floor(x + r), Math.floor(y - r)) &&
    !blocksJump(m, Math.floor(x - r), Math.floor(y + r)) &&
    !blocksJump(m, Math.floor(x + r), Math.floor(y + r))
  );
}

class MinHeap {
  private keys: number[] = [];
  private vals: number[] = [];
  get size(): number {
    return this.vals.length;
  }
  clear(): void {
    this.keys.length = 0;
    this.vals.length = 0;
  }
  push(key: number, val: number): void {
    const k = this.keys;
    const v = this.vals;
    let i = v.length;
    k.push(key);
    v.push(val);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (k[p]! <= key) break;
      k[i] = k[p]!;
      v[i] = v[p]!;
      i = p;
    }
    k[i] = key;
    v[i] = val;
  }
  pop(): number {
    const k = this.keys;
    const v = this.vals;
    const top = v[0]!;
    const lastK = k.pop()!;
    const lastV = v.pop()!;
    const n = v.length;
    if (n) {
      let i = 0;
      while (true) {
        const l = i * 2 + 1;
        if (l >= n) break;
        const r = l + 1;
        const c = r < n && k[r]! < k[l]! ? r : l;
        if (k[c]! >= lastK) break;
        k[i] = k[c]!;
        v[i] = v[c]!;
        i = c;
      }
      k[i] = lastK;
      v[i] = lastV;
    }
    return top;
  }
}

/** Nearest walkable tile center within `radius` tiles of (x, y), or null. */
export function nearestFloor(m: GameMap, x: number, y: number, radius = 2): Waypoint | null {
  const ti = Math.floor(x);
  const tj = Math.floor(y);
  let best: Waypoint | null = null;
  let bd = Infinity;
  for (let dj = -radius; dj <= radius; dj++)
    for (let di = -radius; di <= radius; di++) {
      const i = ti + di;
      const j = tj + dj;
      if (isSolid(m, i, j)) continue;
      const d = Math.hypot(i + 0.5 - x, j + 0.5 - y);
      if (d < bd) {
        bd = d;
        best = [i + 0.5, j + 0.5];
      }
    }
  return best;
}

/** How far a walk's end keeps off a wall between levels (m): more than a body's radius (`freeAt`'s 0.28). */
export const WALL_CLEAR = 0.36;

/**
 * A walk's end moved off the walls of its tile (a terrace's edge, a stair's side) to where a body fits, on a map with
 * levels. A click lands anywhere in a tile, and one on its very edge (a landing at a crag's foot, y = 196.0) left a
 * body that cannot cross the wall short of it for ever: the walker stalled with its last waypoint unreached.
 */
export function settleEnd(m: GameMap, x: number, y: number): Waypoint {
  if (!m.edge && !m.chunks) return [x, y];
  const i = Math.floor(x);
  const j = Math.floor(y);
  const c = WALL_CLEAR;
  if (x - i < c && wallBetween(m, i, j, i - 1, j)) x = i + c;
  else if (i + 1 - x < c && wallBetween(m, i, j, i + 1, j)) x = i + 1 - c;
  if (y - j < c && wallBetween(m, i, j, i, j - 1)) y = j + c;
  else if (j + 1 - y < c && wallBetween(m, i, j, i, j + 1)) y = j + 1 - c;
  return [x, y];
}

/** The largest side of `findPath`'s window on a chunked map, and its margin round start and goal (tiles). */
// (512 and 64: the new Yanyu's 600 m town is a stamped core whose crag stairs switch back well past the two points.)
export const PATH_WINDOW = 512;
export const PATH_MARGIN = 64;

/**
 * Where `findPath` searches from tile (si, sj) to (ti, tj): the whole of a dense map (today's zones,
 * so their paths are unchanged); on a chunked map the box of the two plus `PATH_MARGIN`, at most
 * `PATH_WINDOW` a side (the margin shrinks to fit), or null when the two are too far apart for one
 * window (long routes take the road graph).
 */
export function pathWindow(m: GameMap, si: number, sj: number, ti: number, tj: number): Region | null {
  if (!m.chunks) return [0, m.w, 0, m.h];
  const side = (a: number, b: number, n: number): [number, number] | null => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b) + 1;
    if (hi - lo > PATH_WINDOW) return null;
    const pad = Math.min(PATH_MARGIN, Math.floor((PATH_WINDOW - (hi - lo)) / 2));
    return [Math.max(0, lo - pad), Math.min(n, hi + pad)];
  };
  const x = side(si, ti, m.w);
  const y = side(sj, tj, m.h);
  return x && y ? [x[0], x[1], y[0], y[1]] : null;
}

// The search's buffers, reused between calls: a node's g and parent are valid while its `seen`
// stamp is the call's, and it is closed while its `shut` stamp is.
let cap = 0;
let gBuf = new Float32Array(0);
let fromBuf = new Int32Array(0);
let seen = new Uint32Array(0);
let shut = new Uint32Array(0);
/** A node's ground height (`surfaceAt` at its centre), valid while its `lvlAt` stamp is the call's. */
let lvlBuf = new Float64Array(0);
let lvlAt = new Uint32Array(0);
let stamp = 0;
const heap = new MinHeap();

/**
 * 8-directional A* without corner cutting. Returns waypoints at tile centers, with the final
 * waypoint replaced by the exact destination. Null when the destination is unreachable.
 * On a chunked map it searches only `pathWindow`.
 */
export function findPath(m: GameMap, sx: number, sy: number, tx: number, ty: number): Waypoint[] | null {
  if (isSolid(m, Math.floor(tx), Math.floor(ty))) {
    const alt = nearestFloor(m, tx, ty);
    if (!alt) return null;
    [tx, ty] = alt;
  }
  const win = pathWindow(m, Math.floor(sx), Math.floor(sy), Math.floor(tx), Math.floor(ty));
  return win ? findPathIn(m, sx, sy, tx, ty, win) : null;
}

/** `findPath` inside a window [i0, i1) × [j0, j1) holding start and goal; the search never leaves it. */
export function findPathIn(
  m: GameMap,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  win: Region,
): Waypoint[] | null {
  if (isSolid(m, Math.floor(tx), Math.floor(ty))) {
    const alt = nearestFloor(m, tx, ty);
    if (!alt) return null;
    [tx, ty] = alt;
  }
  const [i0, i1, j0, j1] = win;
  const W = i1 - i0;
  const ti = Math.floor(tx);
  const tj = Math.floor(ty);
  [tx, ty] = settleEnd(m, tx, ty);
  const start = Math.floor(sx) - i0 + (Math.floor(sy) - j0) * W;
  const goal = ti - i0 + (tj - j0) * W;
  if (start === goal) return [[tx, ty]];

  const N = W * (j1 - j0);
  if (N > cap) {
    cap = N;
    gBuf = new Float32Array(N);
    fromBuf = new Int32Array(N);
    seen = new Uint32Array(N);
    shut = new Uint32Array(N);
    lvlBuf = new Float64Array(N);
    lvlAt = new Uint32Array(N);
    stamp = 0;
  }
  if (++stamp === 0xffffffff) {
    seen.fill(0);
    shut.fill(0);
    lvlAt.fill(0);
    stamp = 1;
  }
  const S = stamp;
  const g = gBuf;
  const from = fromBuf;
  const open = heap;
  open.clear();
  const gOf = (n: number) => (seen[n] === S ? g[n]! : Infinity);
  const h = (n: number) => {
    const dx = Math.abs((n % W) + i0 - ti);
    const dy = Math.abs(((n / W) | 0) + j0 - tj);
    return Math.max(dx, dy) + 0.414 * Math.min(dx, dy);
  };
  /** Height of the ground at node n's tile centre (i, j), worked out once per search. */
  const lvl = (n: number, i: number, j: number) => {
    if (lvlAt[n] === S) return lvlBuf[n]!;
    const v = surfaceAt(m, i + 0.5, j + 0.5) ?? 0;
    lvlBuf[n] = v;
    lvlAt[n] = S;
    return v;
  };
  g[start] = 0;
  from[start] = -1;
  seen[start] = S;
  open.push(h(start), start);

  while (open.size) {
    const cur = open.pop();
    if (cur === goal) break;
    if (shut[cur] === S) continue;
    shut[cur] = S;
    const ci = (cur % W) + i0;
    const cj = ((cur / W) | 0) + j0;
    const here = lvl(cur, ci, cj);
    for (let dj = -1; dj <= 1; dj++)
      for (let di = -1; di <= 1; di++) {
        if (!di && !dj) continue;
        const ni = ci + di;
        const nj = cj + dj;
        if (ni < i0 || nj < j0 || ni >= i1 || nj >= j1) continue;
        const n = ni - i0 + (nj - j0) * W;
        // A closed node is done with: skip the tile tests (all pure) before anything else.
        if (shut[n] === S) continue;
        if (isSolid(m, ni, nj)) continue;
        if (di && dj && (isSolid(m, ci + di, cj) || isSolid(m, ci, cj + dj))) continue;
        // Up or down a terrace wall: only by its stairs. Between levels: never across a wall.
        if (Math.abs(lvl(n, ni, nj) - here) > STEP_UP) continue;
        if (!stepOpen(m, ci, cj, di, dj)) continue;
        const ng = g[cur]! + (di && dj ? Math.SQRT2 : 1);
        if (ng < gOf(n)) {
          g[n] = ng;
          from[n] = cur;
          seen[n] = S;
          open.push(ng + h(n), n);
        }
      }
  }
  open.clear();
  if (seen[goal] !== S || from[goal]! < 0) return null;

  const path: Waypoint[] = [];
  for (let n = goal; n !== start && n >= 0; n = from[n]!)
    path.push([(n % W) + i0 + 0.5, ((n / W) | 0) + j0 + 0.5]);
  path.reverse();
  path[path.length - 1] = [tx, ty];
  return path;
}
